import { BillingPlan } from '@cycle-app/graphql-codegen';
import { useReducer } from 'react';

import { BillingUpgradeUsers } from 'src/components/BillingUpgradeUsers';
import { STRIPE_PORTAL_EXPIRATION_DELAY } from 'src/constants/billing.constants';
import { useCurrentBilling, useProduct, useProductAddOns } from 'src/hooks';
import { useStripePortal } from 'src/hooks/api/queries/useStripePortal';
import { useGetPermission } from 'src/reactives/permission.reactive';
import { getExpectedStandardFee, getAmountFlat, getAmountPerUnit, getAmountAddon } from 'src/utils/billing.util';
import { currencyFormat } from 'src/utils/currency.utils';

import { BillingUpgradeAddons } from '../BillingUpgradeAddons';
import {
  Container,
  PricingBox,
  PriceTag,
  PriceSymbol,
  Pricing,
  PricePeriod,
  PricingHeader,
  UpgradeButton,
  PriceInfo,
  PriceTooltip,
  StyledInfoIconOutline,
  PricingFrequency2,
  PricingFrequencyLabel2,
  PricingFrequencyToggle,
  Bottom,
} from './BillingUpgradePricingBox.styles';

export const BillingUpgradePricingBox = () => {
  const { product } = useProduct();
  const hasEndDate = !!product?.endOfFreeTrial || !!product?.subscriptionPeriodEnd;

  const [isAnnual, toggleIsAnnual] = useReducer(b => !b, true);
  const currentBilling = useCurrentBilling();
  const isStandard = currentBilling?.plan === BillingPlan.Standard;
  const stripeQuery = useStripePortal({
    isAnnual,
    pollInterval: STRIPE_PORTAL_EXPIRATION_DELAY - 30_000,
  });
  const addOns = useProductAddOns();
  const addOnItems = addOns.filter(a => a.isEnabled);
  const { canUpgradeBilling } = useGetPermission();
  const upgradeLabel = isStandard && !hasEndDate ? 'Current plan' : 'Upgrade';

  return (
    <Container>
      <PricingBox>
        <PricingFrequency2 onClick={toggleIsAnnual} $isAnnual={isAnnual}>
          <PricingFrequencyLabel2>
            Yearly
          </PricingFrequencyLabel2>

          <PricingFrequencyLabel2>
            Monthly
          </PricingFrequencyLabel2>

          <PricingFrequencyToggle>
            {isAnnual ? 'Yearly' : 'Monthly'}
          </PricingFrequencyToggle>
        </PricingFrequency2>

        <Pricing>
          <PricingHeader>
            {currentBilling && (
              <PriceTag>
                <PriceSymbol>$</PriceSymbol>
                {currencyFormat(getExpectedStandardFee(currentBilling, addOnItems, isAnnual), { trimSymbol: true })}
              </PriceTag>
            )}
            <PriceInfo>
              <PriceTooltip
                placement="top"
                withPortal
                content={(
                  <>
                    {`${currencyFormat(getAmountFlat(isAnnual))} / mo base fee, plus:`}
                    <br />
                    {`${currencyFormat(getAmountPerUnit(isAnnual))} / mo per maker`}
                    {
                      addOnItems.length
                        ? (
                          <>
                            <br />
                            {`${currencyFormat(getAmountAddon(isAnnual))} / mo per add-on`}
                          </>
                        )
                        : <></>
                    }
                  </>
                )}
              >
                <StyledInfoIconOutline />
              </PriceTooltip>
            </PriceInfo>
          </PricingHeader>
          <PricePeriod>
            per month
            {isAnnual && ' ⸱ billed annually'}
          </PricePeriod>
          <UpgradeButton
            isLoading={stripeQuery.loading}
            disabled={!canUpgradeBilling || (isStandard && !hasEndDate) || stripeQuery.loading}
            variant={(!canUpgradeBilling || (isStandard && !hasEndDate)) ? 'ternary' : 'primary'}
            size="L"
            onClick={async (e) => {
              e.stopPropagation();
              const url = stripeQuery.stripePortal;
              if (url) {
                window.open(url, '_blank');
              }
            }}
          >
            {canUpgradeBilling ? upgradeLabel : 'Contact your administrator'}
          </UpgradeButton>
        </Pricing>
        <Bottom>
          <BillingUpgradeUsers />
          <BillingUpgradeAddons />
        </Bottom>
      </PricingBox>
    </Container>
  );
};
